/**
 * Best-effort obfuscation for contact details that would otherwise sit in the
 * HTML as plaintext and be harvested by regex scrapers.
 *
 * This is deliberately simple: it defeats scrapers that pattern-match
 * `mailto:` / `tel:` / `@host` in raw HTML, not a determined headless browser.
 * The encoded value is reversible by design — it must be, because the browser
 * opens the link.
 *
 * Values are encoded on the server (layout) so plaintext never reaches the RSC
 * payload, and decoded only when the user interacts.
 */

const MARKER = "lv~";

/** Encodes a contact string so no plaintext email/phone lands in the HTML. */
export function encodeContact(value: string): string {
  if (!value) return value;
  const codes = Array.from(value).map((char) => char.charCodeAt(0).toString(36));
  return MARKER + codes.join("-");
}

/** Reverses {@link encodeContact}. Returns the input unchanged if not encoded. */
export function decodeContact(value: string): string {
  if (!value || !value.startsWith(MARKER)) return value;
  return value
    .slice(MARKER.length)
    .split("-")
    .map((code) => String.fromCharCode(parseInt(code, 36)))
    .join("");
}

/**
 * Encodes the contact fields of a Payload `site-config` global so the values
 * never reach the client (or the HTML) as plaintext. Other fields are kept
 * as-is.
 */
export function encodeSiteConfigContacts<T extends Record<string, any>>(
  siteConfig: T,
): T {
  if (!siteConfig) return siteConfig;
  return {
    ...siteConfig,
    ...(siteConfig.email !== undefined && {
      email: siteConfig.email ? encodeContact(siteConfig.email) : siteConfig.email,
    }),
    ...(siteConfig.phone !== undefined && {
      phone: siteConfig.phone ? encodeContact(siteConfig.phone) : siteConfig.phone,
    }),
    ...(siteConfig.whatsapp !== undefined && {
      whatsapp: siteConfig.whatsapp
        ? encodeContact(siteConfig.whatsapp)
        : siteConfig.whatsapp,
    }),
  };
}
